import AuthModal from 'components/AuthModal/AuthModal';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { resolveAuthApiBaseUrl } from 'helpers/authApi';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const userAccessTokenKey = 'kromatic-user-t';
// Persisted GA4 user_id pseudonym (#168). Salted-HMAC auid only — never PII.
// Read by the Analytics provider to re-push set_user_id BEFORE GTM bootstrap on
// the next authed load (RSB#735, mirrors plinkromatic).
const userAuidKey = 'kromatic-user-auid';

function normalizeAuthToken(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }

    const normalized = value.replace(/^bearer\s+/i, '').trim();
    return normalized || null;
}

/**
 * PII hard rule (#168): the GA4 user_id may ONLY be the salted-HMAC `auid`
 * pseudonym from orca-back — never a raw id, email, or name. Reject anything
 * that is blank or all-digits (a raw numeric id) so a non-pseudonymous value
 * can never reach the dataLayer or localStorage. Mirrors plinkromatic.
 */
function normalizeAuid(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim();
    if (!normalized || /^\d+$/.test(normalized)) {
        return null;
    }
    return normalized;
}

function persistAuthToken(value: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (value) {
        window.localStorage.setItem(userAccessTokenKey, value);
        return;
    }

    window.localStorage.removeItem(userAccessTokenKey);
}

function persistAuid(value: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (value) {
        window.localStorage.setItem(userAuidKey, value);
        return;
    }

    window.localStorage.removeItem(userAuidKey);
}

const ApiContext = createContext<ApiState>({} as ApiState);

export const useApi = () => useContext(ApiContext);

export function ApiProvider({ children }: ApiProps) {
    const [authModal, setAuthModal] = useState<AuthModalScreen | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<ApiUserInfo | null>(null);
    const { trackActionEvent, trackLeadEvent, identify, clearIdentity } = useAnalytics();

    const clearAuth = useCallback(() => {
        persistAuthToken(null);
        // Drop the persisted auid so a stale identity can't outlive the session
        // via the pre-bootstrap re-push, and clear the GA4 user_id (#168).
        persistAuid(null);
        setToken(null);
        setUserInfo(null);
        // Optional chaining: in prod ApiProvider is nested in AnalyticsProvider
        // (_app.tsx), so identify/clearIdentity are real. Guarding keeps the
        // provider safe when rendered standalone (e.g. unit tests).
        clearIdentity?.();
    }, [clearIdentity]);

    // Map client-side API paths to orca-back paths
    const resolveApiPath = useCallback((path: string): string => {
        const pathMap: Record<string, string> = {
            'auth/user': 'user/info/',
            'auth/recovery-send': 'auth/restore_password/send',
            'auth/recovery-verify': 'auth/restore_password/verify',
        };
        return pathMap[path] || path;
    }, []);

    // TODO: Fix return type
    const request = useCallback(
        async (method: string, path: string, query?: ApiRequestParams | null, data?: ApiRequestParams | null) => {
            const authBase = resolveAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL);
            if (!authBase) {
                return { errors: { detail: 'Auth API is not available.' } } as ApiResponse;
            }

            // Get options
            const options: any = {
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                },
                method,
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            if (options.headers && token) {
                options.headers.Authorization = `Bearer ${token}`;
            }

            // Get URL — call orca-back directly
            let url = `${authBase}/${resolveApiPath(path)}`;
            if (query) {
                url += `?${new URLSearchParams(query)}`;
            }

            // Make request
            const response = await fetch(url, options);
            const responseData = await response.json();

            // Normalize orca-back error responses
            if (response.status !== 200) {
                const errors: Record<string, string> = {};
                if (Array.isArray(responseData.detail)) {
                    responseData.detail.forEach((item: any) => {
                        const key = item.loc?.[1] || 'detail';
                        errors[key] = item.msg.charAt(0).toUpperCase() + item.msg.substring(1);
                    });
                } else if (responseData.detail) {
                    errors.detail = responseData.detail;
                }
                return { errors } as ApiResponse;
            }

            return responseData as ApiResponse;
        },
        [token, resolveApiPath]
    );

    const authenticate = useCallback(
        async (path: string, data: ApiLoginData | ApiRegistrationData) => {
            const isRegistration = path === 'auth/registration';
            if (isRegistration) {
                void trackActionEvent('action_signup_started', {
                    auth_state: 'guest',
                    resource_type: 'tool',
                    source: 'auth_modal_registration',
                });
            }

            // Make request
            const response = await request('POST', path, null, data);
            if (response.errors) {
                if (isRegistration) {
                    void trackActionEvent('action_signup_failed', {
                        auth_state: 'guest',
                        resource_type: 'tool',
                        source: 'auth_modal_registration',
                    });
                }
                return response;
            }

            // Get token
            const nextToken = normalizeAuthToken(response.access_token || null);
            persistAuthToken(nextToken);

            // Update state
            setAuthModal(null);
            setToken(nextToken);
            if (!nextToken) {
                setUserInfo(null);
            }

            if (isRegistration) {
                void trackActionEvent('action_signup_completed', {
                    auth_state: 'member',
                    resource_type: 'tool',
                    source: 'auth_modal_registration',
                });
                void trackLeadEvent('lead_soft_signup_completed', {
                    auth_state: 'member',
                    resource_type: 'tool',
                    source: 'auth_modal_registration',
                });
            }

            return response;
        },
        [request, trackActionEvent, trackLeadEvent]
    );

    const login = useCallback((data: ApiLoginData) => authenticate('auth/login', data), [authenticate]);

    const logout = useCallback(async () => {
        try {
            if (token) {
                await request('POST', 'auth/logout');
            }
        } finally {
            clearAuth();
        }
    }, [clearAuth, request, token]);

    const registration = useCallback(
        (data: ApiRegistrationData) => authenticate('auth/registration', data),
        [authenticate]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const storedToken = window.localStorage.getItem(userAccessTokenKey);
        const normalizedToken = normalizeAuthToken(storedToken);
        if (storedToken !== normalizedToken) {
            persistAuthToken(normalizedToken);
        }
        setToken(normalizedToken);
        if (!normalizedToken) {
            setUserInfo(null);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== userAccessTokenKey) {
                return;
            }

            const normalizedToken = normalizeAuthToken(event.newValue);
            if (event.newValue !== normalizedToken) {
                persistAuthToken(normalizedToken);
            }
            setToken(normalizedToken);
            if (!normalizedToken) {
                setUserInfo(null);
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    useEffect(() => {
        if (!token) {
            setUserInfo(null);
            return;
        }

        let cancelled = false;

        request('GET', 'auth/user')
            .then(data => {
                if (cancelled) {
                    return;
                }
                if (data.errors) {
                    clearAuth();
                    return;
                }

                setUserInfo(data as ApiUserInfo);

                // Push the salted-HMAC auid pseudonym as the GA4 user_id (#168).
                // Never the raw id/email/name. Re-fires on every authed load.
                const auid = normalizeAuid(data?.auid);
                if (auid) {
                    // Persist so the Analytics provider can re-push set_user_id
                    // BEFORE GTM bootstrap on the next authed load (RSB#735).
                    persistAuid(auid);
                    identify?.(auid);
                } else {
                    // A successful /user/info/ with no auid means this session
                    // has no analytics identity. Clear any auid persisted by a
                    // prior user/session so the pre-bootstrap re-push can't
                    // resurrect a stale identity on the next load (RSB#735).
                    persistAuid(null);
                    clearIdentity?.();
                }
            })
            .catch(() => {
                // Keep auth token on transient network failures.
            });

        return () => {
            cancelled = true;
        };
    }, [clearAuth, clearIdentity, identify, request, token]);

    const authEnabled = useMemo(() => {
        if (typeof window === 'undefined') {
            return !!process.env.NEXT_PUBLIC_AUTH_API_URL;
        }
        return !!resolveAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL);
    }, []);

    const state = useMemo<ApiState>(
        () => ({
            authenticate: authEnabled,
            authenticated: !!token,
            userInfo,
            delete: (path, query) => request('DELETE', path, query),
            get: (path, query) => request('GET', path, query),
            login,
            logout,
            post: (path, query, data) => request('POST', path, query, data),
            put: (path, query, data) => request('PUT', path, query, data),
            registration,
            showAuthModal: screen => setAuthModal(screen || 'login'),
        }),
        [authEnabled, token, userInfo, login, logout, registration, request, setAuthModal]
    );

    return (
        <ApiContext.Provider value={state}>
            {authEnabled && authModal && (
                <AuthModal screen={authModal} onClose={() => setAuthModal(null)} />
            )}
            {children}
        </ApiContext.Provider>
    );
}
